import {
  FindProductMetrcPackageCandidatesInputSchema,
  FindProductMetrcPackageCandidatesRequestSchema,
  FindProductMetrcPackageCandidatesResponseSchema,
  ImportProductMetrcPackagesInputSchema,
  ImportProductMetrcPackagesRejectionSchema,
  ImportProductMetrcPackagesRequestSchema,
  ImportProductMetrcPackagesResultPackageSchema,
  ImportProductMetrcPackagesResultSchema,
  MetrcAlreadyImportedRowSchema,
  MetrcCandidatePackageSchema,
  MetrcDriftClassSchema,
  MetrcDriftDetectedRowSchema,
  MetrcFindCandidatesRequestSchema,
  MetrcFindCandidatesResponseSchema,
  MetrcImportPackagesRequestSchema,
  MetrcImportResultPackageSchema,
  MetrcImportResultRejectionSchema,
  MetrcImportResultResponseSchema,
  MetrcMatchStrategySchema,
  MetrcVariantCaseConfigSchema,
  ProductMetrcPackageAlreadyImportedRowSchema,
  ProductMetrcPackageCandidateSchema,
  ProductMetrcPackageImportConfigSchema,
  ProductMetrcPackageMatchStrategySchema,
  ProductMetrcPackageReconciliationClassSchema,
  ProductMetrcPackageReconciliationRowSchema,
  ProductMetrcVariantCaseConfigSchema
} from "../chunk-WCS62LTD.js";

// metrc-inventory/package-mirror.schema.ts
import { z } from "zod";
var MetrcPackageStateSchema = z.enum([
  "active",
  "inactive",
  "transferred",
  "unknown"
]);
var MetrcPackageMirrorSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  facilityLicenseNumber: z.string().min(1),
  metrcPackageId: z.number().int().nullable(),
  packageLabel: z.string().min(1),
  packageType: z.string().nullable(),
  externalId: z.string().nullable(),
  metrcState: MetrcPackageStateSchema,
  quantity: z.number(),
  originalPackageQuantity: z.number().nullable(),
  unitOfMeasureName: z.string().nullable(),
  unitOfMeasureAbbreviation: z.string().nullable(),
  itemId: z.number().int().nullable(),
  itemName: z.string().nullable(),
  productCategoryName: z.string().nullable(),
  productCategoryType: z.string().nullable(),
  sourceHarvestCount: z.number().int().nullable(),
  sourcePackageCount: z.number().int().nullable(),
  sourceProcessingJobCount: z.number().int().nullable(),
  sourceHarvestNames: z.string().nullable(),
  sourcePackageLabels: z.string().nullable(),
  sourceProductionBatchNumbers: z.string().nullable(),
  productionBatchNumber: z.string().nullable(),
  itemFromFacilityLicenseNumber: z.string().nullable(),
  itemFromFacilityName: z.string().nullable(),
  receivedFromManifestNumber: z.string().nullable(),
  receivedFromFacilityLicenseNumber: z.string().nullable(),
  receivedFromFacilityName: z.string().nullable(),
  transferredPackageId: z.number().int().nullable().optional(),
  transferredManifestNumber: z.string().nullable().optional(),
  transferredRecipientFacilityLicenseNumber: z.string().nullable().optional(),
  transferredRecipientFacilityName: z.string().nullable().optional(),
  transferredShipmentPackageStateName: z.string().nullable().optional(),
  transferredShippedQuantity: z.number().nullable().optional(),
  transferredShippedUnitOfMeasureAbbreviation: z.string().nullable().optional(),
  transferredReceivedQuantity: z.number().nullable().optional(),
  transferredReceivedUnitOfMeasureAbbreviation: z.string().nullable().optional(),
  transferredActualDepartureDateTime: z.string().nullable().optional(),
  transferredReceivedDateTime: z.string().nullable().optional(),
  transferredGrossWeight: z.number().nullable().optional(),
  transferredGrossUnitOfWeightAbbreviation: z.string().nullable().optional(),
  transferredShipperWholesalePrice: z.number().nullable().optional(),
  transferredReceiverWholesalePrice: z.number().nullable().optional(),
  locationId: z.number().int().nullable(),
  locationName: z.string().nullable(),
  sublocationId: z.number().int().nullable(),
  sublocationName: z.string().nullable(),
  packagedDate: z.string().nullable(),
  receivedDateTime: z.string().nullable(),
  labTestingState: z.string().nullable(),
  labTestResultExpirationDateTime: z.string().nullable(),
  labTestingPerformedDate: z.string().nullable(),
  labTestingRecordedDate: z.string().nullable(),
  labTestStageId: z.number().int().nullable(),
  labTestStage: z.string().nullable(),
  labelsLastGeneratedDateTime: z.string().nullable(),
  isProductionBatch: z.boolean(),
  isTradeSample: z.boolean(),
  isTradeSamplePersistent: z.boolean(),
  sourcePackageIsTradeSample: z.boolean(),
  isDonation: z.boolean(),
  isDonationPersistent: z.boolean(),
  sourcePackageIsDonation: z.boolean(),
  isTestingSample: z.boolean(),
  isProcessValidationTestingSample: z.boolean(),
  productRequiresRemediation: z.boolean(),
  containsRemediatedProduct: z.boolean(),
  remediationDate: z.string().nullable(),
  productRequiresDecontamination: z.boolean(),
  containsDecontaminatedProduct: z.boolean(),
  decontaminationDate: z.string().nullable(),
  containsPreTreatedProduct: z.boolean(),
  preTreatmentDate: z.string().nullable(),
  isOnHold: z.boolean(),
  isOnRecall: z.boolean(),
  isOnInvestigation: z.boolean(),
  isOnInvestigationHold: z.boolean(),
  isOnInvestigationRecall: z.boolean(),
  isOnHoldCombined: z.boolean(),
  isOnRecallCombined: z.boolean(),
  isOnRetailerDelivery: z.boolean(),
  isFinished: z.boolean(),
  archivedDate: z.string().nullable(),
  finishedDate: z.string().nullable(),
  lastModified: z.string().nullable(),
  rawPayload: z.record(z.string(), z.unknown()),
  firstObservedAt: z.string(),
  lastObservedAt: z.string(),
  lastSyncedAt: z.string()
});
var MetrcPackageMirrorUpsertSchema = MetrcPackageMirrorSchema.omit({
  id: true,
  firstObservedAt: true,
  lastObservedAt: true,
  lastSyncedAt: true
});

// metrc-inventory/package-reservation.schema.ts
import { z as z2 } from "zod";
var MetrcInventoryPackageReservationSchema = z2.object({
  id: z2.string().uuid(),
  organizationId: z2.string().uuid(),
  facilityLicenseNumber: z2.string().min(1),
  orderPartitionItemId: z2.string().uuid(),
  inventoryPackageId: z2.string().uuid(),
  inventoryItemId: z2.string().uuid(),
  stockLocationId: z2.string().uuid(),
  packageLabelSnapshot: z2.string().min(1),
  reservedQuantity: z2.number().nonnegative(),
  releasedQuantity: z2.number().nonnegative(),
  consumedQuantity: z2.number().nonnegative(),
  reservedAt: z2.string(),
  releasedAt: z2.string().nullable(),
  consumedAt: z2.string().nullable(),
  consumedReason: z2.string().nullable(),
  metrcItemIdSnapshot: z2.number().int().nullable(),
  productionBatchNumberSnapshot: z2.string().nullable(),
  lotNumberSnapshot: z2.string().nullable(),
  receivedDateSnapshot: z2.string().nullable(),
  expiryDateSnapshot: z2.string().nullable(),
  unitOfMeasureName: z2.string().nullable(),
  sourceLegacyReservationId: z2.string().uuid().nullable(),
  sourceLegacyInventoryPackageId: z2.string().uuid().nullable(),
  createdAt: z2.string(),
  updatedAt: z2.string()
});
var MetrcInventoryPackageReservationUpsertSchema = z2.object({
  organizationId: z2.string().uuid(),
  facilityLicenseNumber: z2.string().min(1),
  orderPartitionItemId: z2.string().uuid(),
  inventoryPackageId: z2.string().uuid(),
  inventoryItemId: z2.string().uuid(),
  stockLocationId: z2.string().uuid(),
  packageLabelSnapshot: z2.string().min(1),
  reservedQuantity: z2.number().nonnegative(),
  releasedQuantity: z2.number().nonnegative(),
  consumedQuantity: z2.number().nonnegative(),
  reservedAt: z2.string(),
  releasedAt: z2.string().nullable(),
  consumedAt: z2.string().nullable(),
  consumedReason: z2.string().nullable(),
  metrcItemIdSnapshot: z2.number().int().nullable(),
  productionBatchNumberSnapshot: z2.string().nullable(),
  lotNumberSnapshot: z2.string().nullable(),
  receivedDateSnapshot: z2.string().nullable(),
  expiryDateSnapshot: z2.string().nullable(),
  unitOfMeasureName: z2.string().nullable(),
  sourceLegacyReservationId: z2.string().uuid().nullable(),
  sourceLegacyInventoryPackageId: z2.string().uuid().nullable()
});
var ListMetrcInventoryPackageReservationsInputSchema = z2.object({
  organizationId: z2.string().uuid(),
  facilityLicenseNumber: z2.string().min(1),
  orderPartitionItemId: z2.string().uuid().optional(),
  inventoryPackageId: z2.string().uuid().optional(),
  inventoryItemId: z2.string().uuid().optional(),
  stockLocationId: z2.string().uuid().optional(),
  packageLabelSnapshot: z2.string().min(1).optional(),
  sourceLegacyReservationId: z2.string().uuid().optional()
});

// metrc-inventory/sync.schema.ts
import { z as z3 } from "zod";
var MetrcInventorySyncEndpointSchema = z3.enum([
  "packages.active",
  "packages.inactive",
  "packages.transferred",
  "transfers.outgoing",
  "transfers.deliveries",
  "transfers.delivery_packages"
]);
var MetrcInventorySyncRunStatusSchema = z3.enum(["running", "succeeded", "failed"]);
var MetrcPackageSyncStateSchema = z3.enum(["active", "inactive", "transferred"]);
var MetrcInventorySyncRunSchema = z3.object({
  id: z3.string().uuid(),
  organizationId: z3.string().uuid(),
  facilityLicenseNumber: z3.string().min(1),
  endpoint: MetrcInventorySyncEndpointSchema,
  mode: z3.enum(["full", "delta", "manual"]),
  status: MetrcInventorySyncRunStatusSchema,
  windowStart: z3.string(),
  windowEnd: z3.string(),
  pagesFetched: z3.number().int(),
  recordsFetched: z3.number().int(),
  recordsUpserted: z3.number().int(),
  errorMessage: z3.string().nullable(),
  startedAt: z3.string(),
  finishedAt: z3.string().nullable()
});
var MetrcInventorySyncCursorSchema = z3.object({
  id: z3.string().uuid(),
  organizationId: z3.string().uuid(),
  facilityLicenseNumber: z3.string().min(1),
  endpoint: MetrcInventorySyncEndpointSchema,
  lastSuccessfulWindowStart: z3.string().nullable(),
  lastSuccessfulWindowEnd: z3.string().nullable(),
  lastObservedLastModified: z3.string().nullable(),
  lastRunId: z3.string().uuid().nullable(),
  createdAt: z3.string(),
  updatedAt: z3.string()
});
var SyncActiveMetrcPackagesResultSchema = z3.object({
  syncRun: MetrcInventorySyncRunSchema,
  facilityLicenseNumber: z3.string().min(1),
  pagesFetched: z3.number().int(),
  recordsFetched: z3.number().int(),
  recordsUpserted: z3.number().int(),
  firstPackageLabel: z3.string().nullable(),
  lastObservedLastModified: z3.string().nullable()
});
var SyncMetrcPackageStateResultSchema = z3.object({
  syncRun: MetrcInventorySyncRunSchema,
  facilityLicenseNumber: z3.string().min(1),
  endpoint: MetrcInventorySyncEndpointSchema,
  metrcState: MetrcPackageSyncStateSchema,
  pagesFetched: z3.number().int(),
  recordsFetched: z3.number().int(),
  recordsUpserted: z3.number().int(),
  firstPackageLabel: z3.string().nullable(),
  lastObservedLastModified: z3.string().nullable()
});
var SyncMetrcFacilityPackageStatesResultSchema = z3.object({
  facilityLicenseNumber: z3.string().min(1),
  stateResults: z3.array(SyncMetrcPackageStateResultSchema),
  discrepanciesOpened: z3.number().int(),
  discrepanciesResolved: z3.number().int(),
  generatedAt: z3.string()
});
var SyncOutgoingTransferEvidenceInputSchema = z3.object({
  organizationId: z3.string().uuid(),
  facilityLicenseNumber: z3.string().min(1),
  mode: z3.enum(["full", "delta", "manual"]),
  memberId: z3.string().uuid().optional(),
  windowStart: z3.string().optional(),
  windowEnd: z3.string().optional(),
  pageSize: z3.number().int().min(1).max(20).optional()
});
var SyncOutgoingTransferEvidenceResultSchema = z3.object({
  syncRun: MetrcInventorySyncRunSchema,
  facilityLicenseNumber: z3.string().min(1),
  endpoint: z3.literal("transfers.outgoing"),
  windowStart: z3.string(),
  windowEnd: z3.string(),
  transferPagesFetched: z3.number().int(),
  transferRecordsFetched: z3.number().int(),
  deliveryPagesFetched: z3.number().int(),
  deliveryRecordsFetched: z3.number().int(),
  deliveryPackagePagesFetched: z3.number().int(),
  deliveryPackageRecordsFetched: z3.number().int(),
  deliveryPackageRecordsSkipped: z3.number().int(),
  evidenceRecordsUpserted: z3.number().int(),
  firstTransferId: z3.number().int().nullable(),
  lastTransferId: z3.number().int().nullable(),
  firstManifestNumber: z3.string().nullable(),
  firstPackageLabel: z3.string().nullable(),
  lastObservedLastModified: z3.string().nullable()
});

// metrc-inventory/discrepancy.schema.ts
import { z as z5 } from "zod";

// metrc-inventory/evidence.schema.ts
import { z as z4 } from "zod";
var MetrcPackageMovementTypeSchema = z4.enum([
  "outgoing_transfer",
  "incoming_transfer",
  "inactivated",
  "finished",
  "unknown"
]);
var MetrcPackageMovementEvidenceStatusSchema = z4.enum([
  "found",
  "pending",
  "missing"
]);
var TargetedMetrcPackageMovementEvidenceBackfillStatusSchema = z4.enum([
  "found",
  "missing",
  "requires_context",
  "failed"
]);
var RawMetrcPayloadSchema = z4.record(z4.string(), z4.unknown());
var MetrcPackageMovementEvidenceSchema = z4.object({
  id: z4.string().uuid(),
  organizationId: z4.string().uuid(),
  sourceFacilityLicenseNumber: z4.string().min(1),
  packageLabel: z4.string().min(1),
  metrcPackageId: z4.number().int().nullable(),
  movementType: MetrcPackageMovementTypeSchema,
  evidenceStatus: MetrcPackageMovementEvidenceStatusSchema,
  transferId: z4.number().int().nullable(),
  manifestNumber: z4.string().nullable(),
  transferName: z4.string().nullable(),
  transferCreatedDateTime: z4.string().nullable(),
  transferLastModified: z4.string().nullable(),
  transferIsVoided: z4.boolean().nullable(),
  shipperFacilityLicenseNumber: z4.string().nullable(),
  shipperFacilityName: z4.string().nullable(),
  transporterFacilityLicenseNumber: z4.string().nullable(),
  transporterFacilityName: z4.string().nullable(),
  deliveryId: z4.number().int().nullable(),
  recipientFacilityLicenseNumber: z4.string().nullable(),
  recipientFacilityName: z4.string().nullable(),
  recipientAddress: z4.string().nullable(),
  shipmentTypeName: z4.string().nullable(),
  shipmentTransactionType: z4.string().nullable(),
  plannedRoute: z4.string().nullable(),
  estimatedDepartureDateTime: z4.string().nullable(),
  actualDepartureDateTime: z4.string().nullable(),
  estimatedArrivalDateTime: z4.string().nullable(),
  actualArrivalDateTime: z4.string().nullable(),
  receivedDateTime: z4.string().nullable(),
  deliveryPackageCount: z4.number().int().nullable(),
  deliveryReceivedPackageCount: z4.number().int().nullable(),
  packageType: z4.string().nullable(),
  itemId: z4.number().int().nullable(),
  itemName: z4.string().nullable(),
  productName: z4.string().nullable(),
  productCategoryName: z4.string().nullable(),
  labTestingState: z4.string().nullable(),
  shipmentPackageState: z4.string().nullable(),
  shippedQuantity: z4.number().nullable(),
  shippedUnitOfMeasureName: z4.string().nullable(),
  receivedQuantity: z4.number().nullable(),
  receivedUnitOfMeasureName: z4.string().nullable(),
  grossWeight: z4.number().nullable(),
  grossUnitOfWeightName: z4.string().nullable(),
  lastSeenRunId: z4.string().uuid().nullable(),
  lastObservedAt: z4.string(),
  rawTransferPayload: RawMetrcPayloadSchema.nullable(),
  rawDeliveryPayload: RawMetrcPayloadSchema.nullable(),
  rawDeliveryPackagePayload: RawMetrcPayloadSchema.nullable(),
  createdAt: z4.string(),
  updatedAt: z4.string()
});
var MetrcPackageMovementEvidenceUpsertInputSchema = MetrcPackageMovementEvidenceSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true
});
var ListMetrcPackageMovementEvidenceInputSchema = z4.object({
  organizationId: z4.string().uuid(),
  sourceFacilityLicenseNumber: z4.string().min(1).optional(),
  packageLabel: z4.string().min(1).optional(),
  movementType: MetrcPackageMovementTypeSchema.optional(),
  evidenceStatus: MetrcPackageMovementEvidenceStatusSchema.optional(),
  transferId: z4.number().int().optional(),
  manifestNumber: z4.string().min(1).optional(),
  deliveryId: z4.number().int().optional(),
  recipientFacilityLicenseNumber: z4.string().min(1).optional(),
  lastSeenRunId: z4.string().uuid().optional(),
  observedStart: z4.string().optional(),
  observedEnd: z4.string().optional(),
  pageNumber: z4.number().int().min(1).optional(),
  pageSize: z4.number().int().min(1).max(100).optional()
});
var MetrcPackageMovementEvidenceListResponseSchema = z4.object({
  organizationId: z4.string().uuid(),
  generatedAt: z4.string(),
  total: z4.number().int(),
  pageNumber: z4.number().int().min(1),
  pageSize: z4.number().int().min(1),
  items: z4.array(MetrcPackageMovementEvidenceSchema)
});
var GetBestMetrcPackageMovementEvidenceInputSchema = z4.object({
  organizationId: z4.string().uuid(),
  sourceFacilityLicenseNumber: z4.string().min(1),
  packageLabel: z4.string().min(1),
  movementType: MetrcPackageMovementTypeSchema.default("outgoing_transfer")
});
var TargetedMetrcPackageMovementEvidenceBackfillInputSchema = z4.object({
  organizationId: z4.string().uuid(),
  sourceFacilityLicenseNumber: z4.string().min(1),
  packageLabel: z4.string().min(1).optional(),
  manifestNumber: z4.string().min(1).optional(),
  transferId: z4.number().int().optional(),
  windowStart: z4.string().optional(),
  windowEnd: z4.string().optional(),
  pageSize: z4.number().int().min(1).max(20).optional()
});
var TargetedMetrcPackageMovementEvidenceBackfillResultSchema = z4.object({
  organizationId: z4.string().uuid(),
  sourceFacilityLicenseNumber: z4.string().min(1),
  packageLabel: z4.string().nullable(),
  manifestNumber: z4.string().nullable(),
  transferId: z4.number().int().nullable(),
  status: TargetedMetrcPackageMovementEvidenceBackfillStatusSchema,
  evidenceRecordsUpserted: z4.number().int(),
  transferRecordsFetched: z4.number().int(),
  deliveryRecordsFetched: z4.number().int(),
  deliveryPackageRecordsFetched: z4.number().int(),
  evidence: z4.array(MetrcPackageMovementEvidenceSchema),
  message: z4.string().nullable(),
  generatedAt: z4.string()
});

// metrc-inventory/discrepancy.schema.ts
var MetrcInventoryDiscrepancyClassSchema = z5.enum([
  "local_active_metrc_inactive",
  "local_active_metrc_transferred",
  "local_active_metrc_absent",
  "metrc_active_not_local",
  "metrc_active_unmapped",
  "metrc_active_not_published",
  "metrc_quantity_mismatch",
  "metrc_location_mismatch",
  "transfer_evidence_pending",
  "transfer_evidence_missing"
]);
var MetrcInventoryDiscrepancySeveritySchema = z5.enum(["info", "warning", "blocking"]);
var MetrcInventoryDiscrepancyStatusSchema = z5.enum(["open", "resolved", "ignored"]);
var MetrcInventoryDiscrepancySchema = z5.object({
  id: z5.string().uuid(),
  organizationId: z5.string().uuid(),
  facilityLicenseNumber: z5.string().min(1),
  packageLabel: z5.string().min(1),
  discrepancyClass: MetrcInventoryDiscrepancyClassSchema,
  severity: MetrcInventoryDiscrepancySeveritySchema,
  status: MetrcInventoryDiscrepancyStatusSchema,
  metrcPackageMirrorId: z5.string().uuid().nullable(),
  inventoryPackageId: z5.string().uuid().nullable(),
  inventoryItemId: z5.string().uuid().nullable(),
  productVariantId: z5.string().uuid().nullable(),
  metrcPackageId: z5.number().int().nullable(),
  metrcItemId: z5.number().int().nullable(),
  metrcItemName: z5.string().nullable(),
  localState: z5.string().nullable(),
  metrcState: z5.string().nullable(),
  localQuantity: z5.number().nullable(),
  metrcQuantity: z5.number().nullable(),
  metrcUnitOfMeasureName: z5.string().nullable(),
  metrcLocationName: z5.string().nullable(),
  metrcSublocationName: z5.string().nullable(),
  transferredManifestNumber: z5.string().nullable(),
  transferredRecipientFacilityLicenseNumber: z5.string().nullable(),
  transferredRecipientFacilityName: z5.string().nullable(),
  transferredShipmentPackageStateName: z5.string().nullable(),
  movementEvidence: MetrcPackageMovementEvidenceSchema.nullable().optional(),
  lastDetectedRunId: z5.string().uuid().nullable(),
  firstDetectedAt: z5.string(),
  lastDetectedAt: z5.string(),
  resolvedAt: z5.string().nullable(),
  resolutionNote: z5.string().nullable(),
  createdAt: z5.string(),
  updatedAt: z5.string()
});
var MetrcInventoryDiscrepancyListSchema = z5.object({
  organizationId: z5.string().uuid(),
  facilityLicenseNumber: z5.string().min(1),
  generatedAt: z5.string(),
  total: z5.number().int(),
  items: z5.array(MetrcInventoryDiscrepancySchema)
});

// metrc-inventory/comparison.schema.ts
import { z as z6 } from "zod";
var MetrcInventoryComparisonItemSchema = z6.object({
  organizationId: z6.string().uuid(),
  facilityLicenseNumber: z6.string().min(1),
  productVariantId: z6.string().uuid().nullable(),
  inventoryItemId: z6.string().uuid().nullable(),
  metrcItemId: z6.number().int().nullable(),
  metrcItemName: z6.string().nullable(),
  mirrorActivePackageCount: z6.number().int(),
  localActivePackageCount: z6.number().int(),
  inventoryLevelStockedQuantity: z6.number().int(),
  activeReservedQuantity: z6.number().int(),
  countDeltaMirrorVsLocalPackages: z6.number().int(),
  countDeltaMirrorVsInventoryLevel: z6.number().int()
});
var MetrcInventoryComparisonReportSchema = z6.object({
  organizationId: z6.string().uuid(),
  facilityLicenseNumber: z6.string().min(1),
  generatedAt: z6.string(),
  totals: z6.object({
    mirrorActivePackageCount: z6.number().int(),
    localActivePackageCount: z6.number().int(),
    inventoryLevelStockedQuantity: z6.number().int(),
    activeReservedQuantity: z6.number().int()
  }),
  items: z6.array(MetrcInventoryComparisonItemSchema)
});

// metrc-inventory/counter-authority.schema.ts
import { z as z7 } from "zod";
var MetrcCounterAuthorityDiagnosticSeveritySchema = z7.enum([
  "ok",
  "info",
  "warning",
  "blocking"
]);
var MetrcCounterAuthorityDiagnosticClassSchema = z7.enum([
  "level_stocked_not_projection",
  "variant_stocked_not_projection",
  "package_reserved_counter_drift",
  "partition_item_reserved_counter_drift",
  "local_active_metrc_unavailable",
  "metrc_active_not_local",
  "reservation_against_unavailable_package",
  "reservation_without_exact_package_rows"
]);
var MetrcCounterAuthorityDiagnosticMetricSchema = z7.enum([
  "metrc_active_package_count",
  "local_active_package_count",
  "local_package_quantity_sum",
  "local_package_reserved_counter_value",
  "exact_reservation_active_sum",
  "order_partition_item_reserved_value",
  "inventory_level_stocked_quantity",
  "product_variant_stocked_quantity"
]);
var MetrcCounterAuthorityDiagnosticValueRoleSchema = z7.enum([
  "authority",
  "local_projection",
  "deprecated_drift_evidence"
]);
var MetrcCounterAuthorityDiagnosticSourceSchema = z7.enum([
  "inventory.metrc_package_mirrors",
  "inventory.inventory_packages",
  "inventory.inventory_batches",
  "inventory.inventory_levels",
  "inventory.inventory_package_reservations",
  "product.product_variants",
  "delivery.order_partition_items",
  "delivery.order_partition_item_batch_reservations"
]);
var MetrcCounterAuthorityDiagnosticDeltaSchema = z7.object({
  deltaType: z7.enum([
    "local_active_package_count_from_metrc_active_packages",
    "inventory_level_stocked_from_local_package_quantity",
    "product_variant_stocked_from_local_package_quantity",
    "local_package_reserved_counter_from_exact_reservations",
    "order_partition_item_reserved_from_exact_reservations"
  ]),
  authorityMetric: MetrcCounterAuthorityDiagnosticMetricSchema,
  projectionMetric: MetrcCounterAuthorityDiagnosticMetricSchema,
  authorityValue: z7.number(),
  projectionValue: z7.number(),
  delta: z7.number(),
  projectionRole: MetrcCounterAuthorityDiagnosticValueRoleSchema
});
var MetrcCounterAuthorityDiagnosticScopeSchema = z7.object({
  organizationId: z7.string().uuid(),
  facilityLicenseNumber: z7.string().min(1),
  inventoryItemId: z7.string().uuid().nullable(),
  productVariantId: z7.string().uuid().nullable(),
  metrcItemId: z7.number().int().nullable(),
  metrcItemName: z7.string().nullable(),
  packageLabel: z7.string().min(1).nullable(),
  legacyLocalPackageId: z7.string().uuid().nullable(),
  orderPartitionItemId: z7.string().uuid().nullable()
});
var MetrcCounterAuthorityDiagnosticFactSchema = z7.object({
  scope: MetrcCounterAuthorityDiagnosticScopeSchema,
  metric: MetrcCounterAuthorityDiagnosticMetricSchema,
  role: MetrcCounterAuthorityDiagnosticValueRoleSchema,
  value: z7.number(),
  unitOfMeasureName: z7.string().nullable(),
  source: MetrcCounterAuthorityDiagnosticSourceSchema,
  observedAt: z7.string().nullable()
});
var MetrcCounterAuthorityDiagnosticMeasurementsSchema = z7.object({
  metrcActivePackageCount: z7.number().int().nullable(),
  localActivePackageCount: z7.number().int().nullable(),
  localPackageQuantitySum: z7.number().nullable(),
  localPackageReservedCounterValue: z7.number().nullable(),
  exactReservationActiveSum: z7.number().nullable(),
  orderPartitionItemReservedValue: z7.number().nullable(),
  inventoryLevelStockedQuantity: z7.number().nullable(),
  productVariantStockedQuantity: z7.number().nullable()
});
var MetrcCounterAuthorityDiagnosticRowSchema = z7.object({
  scope: MetrcCounterAuthorityDiagnosticScopeSchema,
  diagnosticClass: MetrcCounterAuthorityDiagnosticClassSchema,
  severity: MetrcCounterAuthorityDiagnosticSeveritySchema,
  measurements: MetrcCounterAuthorityDiagnosticMeasurementsSchema,
  deltas: z7.array(MetrcCounterAuthorityDiagnosticDeltaSchema),
  facts: z7.array(MetrcCounterAuthorityDiagnosticFactSchema),
  message: z7.string().nullable()
});
var MetrcCounterAuthorityDiagnosticTotalsSchema = z7.object({
  metrcActivePackageCount: z7.number().int().nullable(),
  localActivePackageCount: z7.number().int().nullable(),
  localPackageQuantitySum: z7.number().nullable(),
  exactReservationActiveSum: z7.number().nullable(),
  rowCount: z7.number().int(),
  blockingRowCount: z7.number().int(),
  warningRowCount: z7.number().int(),
  infoRowCount: z7.number().int()
});
var MetrcCounterAuthorityDiagnosticQuerySchema = z7.object({
  organizationId: z7.string().uuid(),
  facilityLicenseNumber: z7.string().min(1),
  inventoryItemId: z7.string().uuid().optional(),
  productVariantId: z7.string().uuid().optional(),
  packageLabel: z7.string().min(1).optional(),
  orderPartitionItemId: z7.string().uuid().optional(),
  severity: MetrcCounterAuthorityDiagnosticSeveritySchema.optional(),
  diagnosticClass: MetrcCounterAuthorityDiagnosticClassSchema.optional(),
  limit: z7.number().int().min(1).max(500).optional(),
  offset: z7.number().int().min(0).optional()
});
var MetrcCounterAuthorityDiagnosticReportSchema = z7.object({
  organizationId: z7.string().uuid(),
  facilityLicenseNumber: z7.string().min(1),
  generatedAt: z7.string(),
  query: MetrcCounterAuthorityDiagnosticQuerySchema,
  totals: MetrcCounterAuthorityDiagnosticTotalsSchema,
  rows: z7.array(MetrcCounterAuthorityDiagnosticRowSchema)
});

// metrc-inventory/availability.schema.ts
import { z as z9 } from "zod";

// metrc-inventory/mapping.schema.ts
import { z as z8 } from "zod";
var MetrcItemMappingStatusSchema = z8.enum([
  "mapped",
  "unmapped",
  "ignored"
]);
var MetrcItemMappingSourceSchema = z8.enum([
  "user_selected",
  "imported_from_legacy",
  "suggested_by_name",
  "suggested_by_agent"
]);
var MetrcItemMappingSchema = z8.object({
  id: z8.string().uuid(),
  organizationId: z8.string().uuid(),
  facilityLicenseNumber: z8.string().min(1).nullable(),
  metrcItemId: z8.number().int(),
  metrcItemNameSnapshot: z8.string().nullable(),
  productId: z8.string().uuid().nullable(),
  productVariantId: z8.string().uuid().nullable(),
  mappingStatus: MetrcItemMappingStatusSchema,
  mappingSource: MetrcItemMappingSourceSchema,
  confidence: z8.number().min(0).max(1).nullable(),
  createdByMemberId: z8.string().uuid().nullable(),
  updatedByMemberId: z8.string().uuid().nullable(),
  createdAt: z8.string(),
  updatedAt: z8.string()
});
var CreateMetrcItemMappingInputSchema = z8.object({
  organizationId: z8.string().uuid(),
  facilityLicenseNumber: z8.string().min(1).nullable().optional(),
  metrcItemId: z8.number().int(),
  metrcItemNameSnapshot: z8.string().nullable().optional(),
  productId: z8.string().uuid().nullable().optional(),
  productVariantId: z8.string().uuid().nullable().optional(),
  mappingStatus: MetrcItemMappingStatusSchema,
  mappingSource: MetrcItemMappingSourceSchema,
  confidence: z8.number().min(0).max(1).nullable().optional(),
  createdByMemberId: z8.string().uuid().nullable().optional(),
  updatedByMemberId: z8.string().uuid().nullable().optional()
});
var UpdateMetrcItemMappingInputSchema = z8.object({
  id: z8.string().uuid(),
  organizationId: z8.string().uuid(),
  facilityLicenseNumber: z8.string().min(1).nullable().optional(),
  metrcItemNameSnapshot: z8.string().nullable().optional(),
  productId: z8.string().uuid().nullable().optional(),
  productVariantId: z8.string().uuid().nullable().optional(),
  mappingStatus: MetrcItemMappingStatusSchema.optional(),
  mappingSource: MetrcItemMappingSourceSchema.optional(),
  confidence: z8.number().min(0).max(1).nullable().optional(),
  updatedByMemberId: z8.string().uuid().nullable().optional()
});
var MetrcItemMappingUpsertInputSchema = CreateMetrcItemMappingInputSchema;
var ListMetrcItemMappingsInputSchema = z8.object({
  organizationId: z8.string().uuid(),
  facilityLicenseNumber: z8.string().min(1).nullable().optional(),
  includeOrganizationWide: z8.boolean().optional(),
  mappingStatuses: z8.array(MetrcItemMappingStatusSchema).optional(),
  mappingSources: z8.array(MetrcItemMappingSourceSchema).optional(),
  metrcItemIds: z8.array(z8.number().int()).optional(),
  metrcItemNameSearch: z8.string().min(1).optional(),
  productIds: z8.array(z8.string().uuid()).optional(),
  productVariantIds: z8.array(z8.string().uuid()).optional(),
  pageNumber: z8.number().int().min(1).optional(),
  pageSize: z8.number().int().min(1).max(100).optional()
});
var MetrcItemMappingListResponseSchema = z8.object({
  organizationId: z8.string().uuid(),
  facilityLicenseNumber: z8.string().min(1).nullable(),
  generatedAt: z8.string(),
  total: z8.number().int(),
  pageNumber: z8.number().int().min(1),
  pageSize: z8.number().int().min(1),
  items: z8.array(MetrcItemMappingSchema)
});
var FindMetrcItemMappingInputSchema = z8.object({
  organizationId: z8.string().uuid(),
  facilityLicenseNumber: z8.string().min(1).nullable().optional(),
  metrcItemId: z8.number().int(),
  includeOrganizationWideFallback: z8.boolean().optional()
});
var BulkGetMetrcItemMappingsInputSchema = z8.object({
  organizationId: z8.string().uuid(),
  facilityLicenseNumber: z8.string().min(1).nullable().optional(),
  metrcItemIds: z8.array(z8.number().int()).min(1),
  includeOrganizationWideFallback: z8.boolean().optional()
});
var BulkMetrcItemMappingLookupItemSchema = z8.object({
  metrcItemId: z8.number().int(),
  mapping: MetrcItemMappingSchema.nullable()
});
var BulkMetrcItemMappingLookupResultSchema = z8.object({
  organizationId: z8.string().uuid(),
  facilityLicenseNumber: z8.string().min(1).nullable(),
  items: z8.array(BulkMetrcItemMappingLookupItemSchema)
});

// metrc-inventory/availability.schema.ts
var MetrcAvailabilityPackagePublicationStatusSchema = z9.enum([
  "published",
  "unpublished"
]);
var MetrcAvailabilityPackageExclusionReasonSchema = z9.enum([
  "mirror_inactive",
  "mirror_transferred",
  "mirror_absent",
  "metrc_active_not_local",
  "metrc_active_unmapped",
  "metrc_active_not_published",
  "metrc_hold",
  "metrc_recall",
  "metrc_investigation",
  "metrc_retailer_delivery"
]);
var MetrcAvailabilityQuerySchema = z9.object({
  organizationId: z9.string().uuid(),
  facilityLicenseNumber: z9.string().min(1),
  inventoryItemIds: z9.array(z9.string().uuid()).optional(),
  stockLocationIds: z9.array(z9.string().uuid()).optional(),
  productIds: z9.array(z9.string().uuid()).optional(),
  productVariantIds: z9.array(z9.string().uuid()).optional(),
  metrcItemIds: z9.array(z9.number().int()).optional(),
  packageLabels: z9.array(z9.string().min(1)).optional()
});
var MetrcAvailabilityPackageFactSchema = z9.object({
  organizationId: z9.string().uuid(),
  facilityLicenseNumber: z9.string().min(1),
  packageLabel: z9.string().min(1),
  inventoryPackageId: z9.string().uuid().nullable(),
  inventoryItemId: z9.string().uuid().nullable(),
  stockLocationId: z9.string().uuid().nullable(),
  productVariantId: z9.string().uuid().nullable(),
  metrcItemId: z9.number().int().nullable(),
  metrcItemName: z9.string().nullable(),
  localPackageQuantity: z9.number().nullable(),
  localPackageStatus: z9.enum(["active", "missing", "fulfilled", "transferred"]).nullable(),
  metrcPackageState: MetrcPackageStateSchema.nullable(),
  metrcPackageQuantity: z9.number().nullable(),
  unitOfMeasureName: z9.string().nullable(),
  isOnHoldCombined: z9.boolean().nullable(),
  isOnRecallCombined: z9.boolean().nullable(),
  isOnInvestigationCombined: z9.boolean().nullable(),
  isOnRetailerDelivery: z9.boolean().nullable(),
  mappingId: z9.string().uuid().nullable(),
  mappingStatus: MetrcItemMappingStatusSchema.nullable(),
  mappingProductVariantId: z9.string().uuid().nullable(),
  publicationId: z9.string().uuid().nullable(),
  publicationStatus: MetrcAvailabilityPackagePublicationStatusSchema.nullable(),
  publicationProductVariantId: z9.string().uuid().nullable(),
  observedAt: z9.string().nullable()
});
var MetrcAvailabilityPackageRowSchema = z9.object({
  organizationId: z9.string().uuid(),
  facilityLicenseNumber: z9.string().min(1),
  packageLabel: z9.string().min(1),
  inventoryPackageId: z9.string().uuid().nullable(),
  inventoryItemId: z9.string().uuid().nullable(),
  stockLocationId: z9.string().uuid().nullable(),
  productVariantId: z9.string().uuid().nullable(),
  metrcItemId: z9.number().int().nullable(),
  metrcItemName: z9.string().nullable(),
  localPackageQuantity: z9.number().nullable(),
  metrcPackageQuantity: z9.number().nullable(),
  grossMetrcActiveQuantity: z9.number(),
  eligibleLocalQuantity: z9.number(),
  exactActiveReservedQuantity: z9.number(),
  exactActiveHeldQuantity: z9.number().nonnegative(),
  availableQuantity: z9.number(),
  unitOfMeasureName: z9.string().nullable(),
  metrcPackageState: MetrcPackageStateSchema.nullable(),
  exclusionReason: MetrcAvailabilityPackageExclusionReasonSchema.nullable(),
  excludedFromAvailability: z9.boolean(),
  holdStatus: z9.boolean().nullable(),
  recallStatus: z9.boolean().nullable(),
  investigationStatus: z9.boolean().nullable(),
  retailerDeliveryStatus: z9.boolean().nullable(),
  mappingId: z9.string().uuid().nullable(),
  mappingStatus: MetrcItemMappingStatusSchema.nullable(),
  mappingProductVariantId: z9.string().uuid().nullable(),
  publicationId: z9.string().uuid().nullable(),
  publicationStatus: MetrcAvailabilityPackagePublicationStatusSchema.nullable(),
  publicationProductVariantId: z9.string().uuid().nullable(),
  observedAt: z9.string().nullable()
});
var MetrcAvailabilityTotalsSchema = z9.object({
  grossMetrcActivePackageSupply: z9.number(),
  localPackageSupply: z9.number(),
  eligibleLocalPackageSupply: z9.number(),
  exactActiveReservedQuantity: z9.number(),
  exactActiveHeldQuantity: z9.number().nonnegative(),
  availableQuantity: z9.number(),
  excludedPackageCount: z9.number().int(),
  metrcActiveNotLocalPackageCount: z9.number().int(),
  packageCount: z9.number().int()
});
var MetrcAvailabilityReportSchema = z9.object({
  organizationId: z9.string().uuid(),
  facilityLicenseNumber: z9.string().min(1),
  generatedAt: z9.string(),
  query: MetrcAvailabilityQuerySchema,
  totals: MetrcAvailabilityTotalsSchema,
  rows: z9.array(MetrcAvailabilityPackageRowSchema)
});

// metrc-inventory/hold.schema.ts
import { z as z10 } from "zod";
var MetrcPackageHoldStatusSchema = z10.enum([
  "active",
  "released",
  "expired"
]);
var MetrcPackageHoldReasonSchema = z10.enum([
  "sales_rep_set_aside",
  "customer_pre_reserve",
  "quarantine",
  "operational_hold",
  "compliance_hold",
  "internal_staging",
  "manual_review"
]);
var MetrcPackageHoldSourceSchema = z10.enum([
  "manual",
  "system",
  "migration",
  "admin_repair"
]);
var MetrcPackageHoldOwnerTypeSchema = z10.enum([
  "member",
  "customer",
  "order",
  "organization",
  "system",
  "none"
]);
var MetrcPackageHoldOperationStatusSchema = z10.enum([
  "created",
  "released",
  "expired",
  "failed"
]);
var METRC_PACKAGE_HOLD_NOTE_MAX_LENGTH = 1e3;
var MetrcPackageHoldScopeSchema = z10.object({
  organizationId: z10.string().uuid(),
  facilityLicenseNumber: z10.string().min(1)
});
var MetrcPackageHoldNoteSchema = z10.string().trim().max(METRC_PACKAGE_HOLD_NOTE_MAX_LENGTH);
var OptionalMetrcPackageHoldNoteSchema = z10.preprocess((value) => {
  if (value === void 0) return void 0;
  if (value === null) return null;
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}, MetrcPackageHoldNoteSchema.nullable().optional());
var MetrcPackageHoldOwnerContextSchema = z10.object({
  ownerType: MetrcPackageHoldOwnerTypeSchema,
  ownerMemberId: z10.string().uuid().nullable().optional(),
  ownerCustomerId: z10.string().uuid().nullable().optional(),
  ownerOrderId: z10.string().uuid().nullable().optional(),
  ownerOrderPartitionId: z10.string().uuid().nullable().optional()
});
var MetrcPackageHoldSchema = z10.object({
  id: z10.string().uuid(),
  organizationId: z10.string().uuid(),
  facilityLicenseNumber: z10.string().min(1),
  inventoryPackageId: z10.string().uuid(),
  packageLabel: z10.string().min(1),
  metrcItemId: z10.number().int().nullable(),
  productVariantId: z10.string().uuid().nullable(),
  holdQuantity: z10.number().positive(),
  unitOfMeasureName: z10.string().nullable(),
  holdStatus: MetrcPackageHoldStatusSchema,
  holdReason: MetrcPackageHoldReasonSchema,
  holdSource: MetrcPackageHoldSourceSchema,
  ownerType: MetrcPackageHoldOwnerTypeSchema,
  ownerMemberId: z10.string().uuid().nullable(),
  ownerCustomerId: z10.string().uuid().nullable(),
  ownerOrderId: z10.string().uuid().nullable(),
  ownerOrderPartitionId: z10.string().uuid().nullable(),
  heldByMemberId: z10.string().uuid().nullable(),
  heldAt: z10.string().nullable(),
  expiresAt: z10.string().nullable(),
  note: MetrcPackageHoldNoteSchema.nullable(),
  releasedByMemberId: z10.string().uuid().nullable(),
  releasedAt: z10.string().nullable(),
  releaseReason: z10.string().nullable(),
  createdAt: z10.string(),
  updatedAt: z10.string()
});
var CreateMetrcPackageHoldItemSchema = MetrcPackageHoldOwnerContextSchema.extend({
  inventoryPackageId: z10.string().uuid(),
  packageLabel: z10.string().min(1),
  metrcItemId: z10.number().int().nullable().optional(),
  productVariantId: z10.string().uuid().nullable().optional(),
  holdQuantity: z10.number().positive(),
  unitOfMeasureName: z10.string().nullable().optional(),
  holdReason: MetrcPackageHoldReasonSchema,
  holdSource: MetrcPackageHoldSourceSchema.optional(),
  expiresAt: z10.string().nullable().optional(),
  note: OptionalMetrcPackageHoldNoteSchema
});
var CreateMetrcPackageHoldsInputSchema = MetrcPackageHoldScopeSchema.extend({
  holdSource: MetrcPackageHoldSourceSchema.optional(),
  heldByMemberId: z10.string().uuid().nullable().optional(),
  heldAt: z10.string().optional(),
  holds: z10.array(CreateMetrcPackageHoldItemSchema).min(1)
});
var MetrcPackageHoldOperationTargetSchema = z10.object({
  id: z10.string().uuid().optional(),
  inventoryPackageId: z10.string().uuid().optional(),
  packageLabel: z10.string().min(1).optional()
}).refine(
  (target) => target.id !== void 0 || target.inventoryPackageId !== void 0 || target.packageLabel !== void 0,
  "At least one hold id, inventory package id, or package label is required."
);
var ReleaseMetrcPackageHoldsInputSchema = MetrcPackageHoldScopeSchema.extend({
  releasedByMemberId: z10.string().uuid().nullable().optional(),
  releasedAt: z10.string().optional(),
  releaseReason: z10.string().nullable().optional(),
  holds: z10.array(MetrcPackageHoldOperationTargetSchema).min(1)
});
var ExpireMetrcPackageHoldsInputSchema = MetrcPackageHoldScopeSchema.extend({
  expiredAt: z10.string().optional(),
  expiresAtOrBefore: z10.string().optional(),
  holds: z10.array(MetrcPackageHoldOperationTargetSchema).min(1).optional()
});
var ListMetrcPackageHoldsInputSchema = MetrcPackageHoldScopeSchema.extend({
  holdStatuses: z10.array(MetrcPackageHoldStatusSchema).optional(),
  holdReasons: z10.array(MetrcPackageHoldReasonSchema).optional(),
  holdSources: z10.array(MetrcPackageHoldSourceSchema).optional(),
  ownerTypes: z10.array(MetrcPackageHoldOwnerTypeSchema).optional(),
  inventoryPackageIds: z10.array(z10.string().uuid()).optional(),
  packageLabels: z10.array(z10.string().min(1)).optional(),
  metrcItemIds: z10.array(z10.number().int()).optional(),
  productVariantIds: z10.array(z10.string().uuid()).optional(),
  ownerMemberIds: z10.array(z10.string().uuid()).optional(),
  ownerCustomerIds: z10.array(z10.string().uuid()).optional(),
  ownerOrderIds: z10.array(z10.string().uuid()).optional(),
  ownerOrderPartitionIds: z10.array(z10.string().uuid()).optional(),
  expiresBefore: z10.string().optional(),
  expiresAfter: z10.string().optional(),
  pageNumber: z10.number().int().min(1).optional(),
  pageSize: z10.number().int().min(1).max(100).optional()
});
var MetrcPackageHoldListResponseSchema = z10.object({
  organizationId: z10.string().uuid(),
  facilityLicenseNumber: z10.string().min(1),
  generatedAt: z10.string(),
  total: z10.number().int(),
  pageNumber: z10.number().int().min(1),
  pageSize: z10.number().int().min(1),
  items: z10.array(MetrcPackageHoldSchema)
});
var FindActiveMetrcPackageHoldsByPackageLabelsInputSchema = MetrcPackageHoldScopeSchema.extend({
  packageLabels: z10.array(z10.string().min(1)).min(1),
  asOf: z10.string().optional()
});
var SumActiveMetrcPackageHoldsByPackageLabelsInputSchema = FindActiveMetrcPackageHoldsByPackageLabelsInputSchema;
var SumActiveMetrcPackageHoldsByInventoryPackageIdsInputSchema = MetrcPackageHoldScopeSchema.extend({
  inventoryPackageIds: z10.array(z10.string().uuid()).min(1),
  asOf: z10.string().optional()
});
var MetrcPackageHoldActiveSummarySchema = z10.object({
  organizationId: z10.string().uuid(),
  facilityLicenseNumber: z10.string().min(1),
  inventoryPackageId: z10.string().uuid().nullable(),
  packageLabel: z10.string().min(1),
  activeHeldQuantity: z10.number().nonnegative(),
  activeHoldCount: z10.number().int().nonnegative(),
  holdReasons: z10.array(MetrcPackageHoldReasonSchema).default([]),
  earliestExpiresAt: z10.string().nullable()
});
var MetrcActiveHeldQuantityByPackageLabelSchema = z10.object({
  packageLabel: z10.string().min(1),
  activeHeldQuantity: z10.number().nonnegative()
});
var MetrcActiveHeldQuantityByInventoryPackageIdSchema = z10.object({
  inventoryPackageId: z10.string().uuid(),
  activeHeldQuantity: z10.number().nonnegative()
});
var MetrcPackageHoldOperationItemSchema = z10.object({
  id: z10.string().uuid().nullable(),
  inventoryPackageId: z10.string().uuid().nullable(),
  packageLabel: z10.string().min(1).nullable(),
  success: z10.boolean(),
  operationStatus: MetrcPackageHoldOperationStatusSchema,
  holdStatus: MetrcPackageHoldStatusSchema.nullable(),
  hold: MetrcPackageHoldSchema.nullable(),
  errorCode: z10.string().nullable(),
  errorMessage: z10.string().nullable()
});
var MetrcPackageHoldOperationResultSchema = z10.object({
  organizationId: z10.string().uuid(),
  facilityLicenseNumber: z10.string().min(1),
  requestedCount: z10.number().int(),
  successCount: z10.number().int(),
  failureCount: z10.number().int(),
  results: z10.array(MetrcPackageHoldOperationItemSchema)
});

// metrc-inventory/publication.schema.ts
import { z as z11 } from "zod";
var MetrcPackagePublicationStatusSchema = z11.enum([
  "published",
  "unpublished"
]);
var MetrcPackagePublicationSourceSchema = z11.enum([
  "legacy_backfill",
  "manual",
  "direct_import",
  "sync_default"
]);
var MetrcPackageStagingStateSchema = z11.enum([
  "known_unmapped",
  "known_mapped_unpublished",
  "published",
  "ignored",
  "not_local"
]);
var MetrcPackagePublicationOperationStatusSchema = z11.enum([
  "published",
  "unpublished",
  "failed"
]);
var MetrcPackagePublicationSchema = z11.object({
  id: z11.string().uuid(),
  organizationId: z11.string().uuid(),
  facilityLicenseNumber: z11.string().min(1),
  inventoryPackageId: z11.string().uuid().nullable(),
  packageLabel: z11.string().min(1),
  metrcItemId: z11.number().int().nullable(),
  productVariantId: z11.string().uuid().nullable(),
  publicationStatus: MetrcPackagePublicationStatusSchema,
  publicationSource: MetrcPackagePublicationSourceSchema,
  publishedByMemberId: z11.string().uuid().nullable(),
  publishedAt: z11.string().nullable(),
  unpublishedByMemberId: z11.string().uuid().nullable(),
  unpublishedAt: z11.string().nullable(),
  createdAt: z11.string(),
  updatedAt: z11.string()
});
var MetrcPackagePublicationUpsertInputSchema = z11.object({
  organizationId: z11.string().uuid(),
  facilityLicenseNumber: z11.string().min(1),
  inventoryPackageId: z11.string().uuid().nullable().optional(),
  packageLabel: z11.string().min(1),
  metrcItemId: z11.number().int().nullable().optional(),
  productVariantId: z11.string().uuid().nullable().optional(),
  publicationStatus: MetrcPackagePublicationStatusSchema,
  publicationSource: MetrcPackagePublicationSourceSchema,
  publishedByMemberId: z11.string().uuid().nullable().optional(),
  publishedAt: z11.string().nullable().optional(),
  unpublishedByMemberId: z11.string().uuid().nullable().optional(),
  unpublishedAt: z11.string().nullable().optional()
});
var ListMetrcPackagePublicationsInputSchema = z11.object({
  organizationId: z11.string().uuid(),
  facilityLicenseNumber: z11.string().min(1),
  publicationStatuses: z11.array(MetrcPackagePublicationStatusSchema).optional(),
  publicationSources: z11.array(MetrcPackagePublicationSourceSchema).optional(),
  inventoryPackageIds: z11.array(z11.string().uuid()).optional(),
  packageLabels: z11.array(z11.string().min(1)).optional(),
  metrcItemIds: z11.array(z11.number().int()).optional(),
  productVariantIds: z11.array(z11.string().uuid()).optional(),
  pageNumber: z11.number().int().min(1).optional(),
  pageSize: z11.number().int().min(1).max(100).optional()
});
var MetrcPackagePublicationListResponseSchema = z11.object({
  organizationId: z11.string().uuid(),
  facilityLicenseNumber: z11.string().min(1),
  generatedAt: z11.string(),
  total: z11.number().int(),
  pageNumber: z11.number().int().min(1),
  pageSize: z11.number().int().min(1),
  items: z11.array(MetrcPackagePublicationSchema)
});
var MetrcPackagePublicationTargetDetailSchema = z11.object({
  metrcItemId: z11.number().int().nullable().optional(),
  productVariantId: z11.string().uuid().nullable().optional()
});
var MetrcPackagePublicationTargetSchema = z11.union([
  MetrcPackagePublicationTargetDetailSchema.extend({
    inventoryPackageId: z11.string().uuid(),
    packageLabel: z11.string().min(1).optional()
  }),
  MetrcPackagePublicationTargetDetailSchema.extend({
    inventoryPackageId: z11.string().uuid().optional(),
    packageLabel: z11.string().min(1)
  })
]);
var PublishMetrcPackagesInputSchema = z11.object({
  organizationId: z11.string().uuid(),
  facilityLicenseNumber: z11.string().min(1),
  publicationSource: MetrcPackagePublicationSourceSchema.optional(),
  publishedByMemberId: z11.string().uuid().nullable().optional(),
  publishedAt: z11.string().optional(),
  packages: z11.array(MetrcPackagePublicationTargetSchema).min(1)
});
var UnpublishMetrcPackagesInputSchema = z11.object({
  organizationId: z11.string().uuid(),
  facilityLicenseNumber: z11.string().min(1),
  publicationSource: MetrcPackagePublicationSourceSchema.optional(),
  unpublishedByMemberId: z11.string().uuid().nullable().optional(),
  unpublishedAt: z11.string().optional(),
  packages: z11.array(MetrcPackagePublicationTargetSchema).min(1)
});
var MetrcPackagePublicationOperationItemSchema = z11.object({
  inventoryPackageId: z11.string().uuid().nullable(),
  packageLabel: z11.string().min(1).nullable(),
  success: z11.boolean(),
  operationStatus: MetrcPackagePublicationOperationStatusSchema,
  publicationStatus: MetrcPackagePublicationStatusSchema.nullable(),
  publication: MetrcPackagePublicationSchema.nullable(),
  errorCode: z11.string().nullable(),
  errorMessage: z11.string().nullable()
});
var MetrcPackagePublicationOperationResultSchema = z11.object({
  organizationId: z11.string().uuid(),
  facilityLicenseNumber: z11.string().min(1),
  requestedCount: z11.number().int(),
  successCount: z11.number().int(),
  failureCount: z11.number().int(),
  results: z11.array(MetrcPackagePublicationOperationItemSchema)
});
var FindMetrcPackagePublicationsForPackagesInputSchema = z11.object({
  organizationId: z11.string().uuid(),
  facilityLicenseNumber: z11.string().min(1),
  inventoryPackageIds: z11.array(z11.string().uuid()).optional(),
  packageLabels: z11.array(z11.string().min(1)).optional()
});
var ListMetrcPackageStagingInputSchema = z11.object({
  organizationId: z11.string().uuid(),
  facilityLicenseNumber: z11.string().min(1),
  stagingStates: z11.array(MetrcPackageStagingStateSchema).optional(),
  mappingStatuses: z11.array(MetrcItemMappingStatusSchema).optional(),
  publicationStatuses: z11.array(MetrcPackagePublicationStatusSchema).optional(),
  inventoryPackageIds: z11.array(z11.string().uuid()).optional(),
  packageLabels: z11.array(z11.string().min(1)).optional(),
  metrcItemIds: z11.array(z11.number().int()).optional(),
  productIds: z11.array(z11.string().uuid()).optional(),
  productVariantIds: z11.array(z11.string().uuid()).optional(),
  /** Case-insensitive substring match across packageLabel, metrcItemName, and metrcItemId. */
  searchText: z11.string().optional(),
  /** When true, limits results to rows with an active local Flote hold quantity. */
  heldOnly: z11.boolean().optional(),
  /** When true, limits results to rows with one or more exclusion reasons. */
  excludedOnly: z11.boolean().optional(),
  pageNumber: z11.number().int().min(1).optional(),
  pageSize: z11.number().int().min(1).max(100).optional()
});
var MetrcPackageStagingRowSchema = z11.object({
  organizationId: z11.string().uuid(),
  facilityLicenseNumber: z11.string().min(1),
  stagingState: MetrcPackageStagingStateSchema,
  inventoryPackageId: z11.string().uuid().nullable(),
  packageLabel: z11.string().min(1),
  metrcPackageId: z11.number().int().nullable(),
  metrcPackageState: MetrcPackageStateSchema.nullable(),
  /** Display-compatible Metrc-observed physical package quantity. */
  quantity: z11.number().nullable(),
  /** Local package row count used by engine diagnostics/math; not row-level physical quantity. */
  localPackageQuantity: z11.number().nullable().optional(),
  /** Metrc-observed physical package quantity for operator-facing package readiness display. */
  metrcPackageQuantity: z11.number().nullable().optional(),
  metrcItemId: z11.number().int().nullable(),
  metrcItemName: z11.string().nullable(),
  productionBatchNumber: z11.string().nullable(),
  productId: z11.string().uuid().nullable(),
  productVariantId: z11.string().uuid().nullable(),
  mappingId: z11.string().uuid().nullable(),
  mappingStatus: MetrcItemMappingStatusSchema.nullable(),
  mappingProductVariantId: z11.string().uuid().nullable(),
  publicationId: z11.string().uuid().nullable(),
  publicationStatus: MetrcPackagePublicationStatusSchema.nullable(),
  publicationProductVariantId: z11.string().uuid().nullable(),
  /** Resolved variant title for mappingProductVariantId — populated by the staging service. */
  mappingProductVariantTitle: z11.string().nullable().optional(),
  /** Resolved variant SKU for mappingProductVariantId. */
  mappingProductVariantSku: z11.string().nullable().optional(),
  /** Resolved parent product title for mappingProductVariantId. */
  mappingProductTitle: z11.string().nullable().optional(),
  exclusionReasons: z11.array(MetrcAvailabilityPackageExclusionReasonSchema).default([]),
  exactActiveHeldQuantity: z11.number().nonnegative().optional(),
  activeHoldCount: z11.number().int().nonnegative().optional(),
  holdReasons: z11.array(MetrcPackageHoldReasonSchema).optional(),
  earliestHoldExpiresAt: z11.string().nullable().optional(),
  unitOfMeasureName: z11.string().nullable(),
  lastObservedAt: z11.string().nullable()
});
var MetrcPackageStagingListResponseSchema = z11.object({
  organizationId: z11.string().uuid(),
  facilityLicenseNumber: z11.string().min(1),
  generatedAt: z11.string(),
  total: z11.number().int(),
  pageNumber: z11.number().int().min(1),
  pageSize: z11.number().int().min(1),
  items: z11.array(MetrcPackageStagingRowSchema)
});
var MetrcPackageStagingAggregatesSchema = z11.object({
  knownInMetrcCount: z11.number().int().nonnegative(),
  mappedCount: z11.number().int().nonnegative(),
  publishedCount: z11.number().int().nonnegative(),
  excludedCount: z11.number().int().nonnegative(),
  sellableCount: z11.number().int().nonnegative(),
  reservedCount: z11.number().int().nonnegative(),
  heldQuantity: z11.number().nonnegative()
});
var ListMetrcPackageStagingAggregatesInputSchema = ListMetrcPackageStagingInputSchema.omit({
  pageNumber: true,
  pageSize: true
});
var MetrcPackageStagingAggregatesResponseSchema = z11.object({
  organizationId: z11.string().uuid(),
  facilityLicenseNumber: z11.string().min(1),
  generatedAt: z11.string(),
  aggregates: MetrcPackageStagingAggregatesSchema
});
export {
  BulkGetMetrcItemMappingsInputSchema,
  BulkMetrcItemMappingLookupItemSchema,
  BulkMetrcItemMappingLookupResultSchema,
  CreateMetrcItemMappingInputSchema,
  CreateMetrcPackageHoldItemSchema,
  CreateMetrcPackageHoldsInputSchema,
  ExpireMetrcPackageHoldsInputSchema,
  FindActiveMetrcPackageHoldsByPackageLabelsInputSchema,
  FindMetrcItemMappingInputSchema,
  FindMetrcPackagePublicationsForPackagesInputSchema,
  FindProductMetrcPackageCandidatesInputSchema,
  FindProductMetrcPackageCandidatesRequestSchema,
  FindProductMetrcPackageCandidatesResponseSchema,
  GetBestMetrcPackageMovementEvidenceInputSchema,
  ImportProductMetrcPackagesInputSchema,
  ImportProductMetrcPackagesRejectionSchema,
  ImportProductMetrcPackagesRequestSchema,
  ImportProductMetrcPackagesResultPackageSchema,
  ImportProductMetrcPackagesResultSchema,
  ListMetrcInventoryPackageReservationsInputSchema,
  ListMetrcItemMappingsInputSchema,
  ListMetrcPackageHoldsInputSchema,
  ListMetrcPackageMovementEvidenceInputSchema,
  ListMetrcPackagePublicationsInputSchema,
  ListMetrcPackageStagingAggregatesInputSchema,
  ListMetrcPackageStagingInputSchema,
  METRC_PACKAGE_HOLD_NOTE_MAX_LENGTH,
  MetrcActiveHeldQuantityByInventoryPackageIdSchema,
  MetrcActiveHeldQuantityByPackageLabelSchema,
  MetrcAlreadyImportedRowSchema,
  MetrcAvailabilityPackageExclusionReasonSchema,
  MetrcAvailabilityPackageFactSchema,
  MetrcAvailabilityPackageRowSchema,
  MetrcAvailabilityQuerySchema,
  MetrcAvailabilityReportSchema,
  MetrcAvailabilityTotalsSchema,
  MetrcCandidatePackageSchema,
  MetrcCounterAuthorityDiagnosticClassSchema,
  MetrcCounterAuthorityDiagnosticDeltaSchema,
  MetrcCounterAuthorityDiagnosticFactSchema,
  MetrcCounterAuthorityDiagnosticMeasurementsSchema,
  MetrcCounterAuthorityDiagnosticMetricSchema,
  MetrcCounterAuthorityDiagnosticQuerySchema,
  MetrcCounterAuthorityDiagnosticReportSchema,
  MetrcCounterAuthorityDiagnosticRowSchema,
  MetrcCounterAuthorityDiagnosticScopeSchema,
  MetrcCounterAuthorityDiagnosticSeveritySchema,
  MetrcCounterAuthorityDiagnosticSourceSchema,
  MetrcCounterAuthorityDiagnosticTotalsSchema,
  MetrcCounterAuthorityDiagnosticValueRoleSchema,
  MetrcDriftClassSchema,
  MetrcDriftDetectedRowSchema,
  MetrcFindCandidatesRequestSchema,
  MetrcFindCandidatesResponseSchema,
  MetrcImportPackagesRequestSchema,
  MetrcImportResultPackageSchema,
  MetrcImportResultRejectionSchema,
  MetrcImportResultResponseSchema,
  MetrcInventoryComparisonItemSchema,
  MetrcInventoryComparisonReportSchema,
  MetrcInventoryDiscrepancyClassSchema,
  MetrcInventoryDiscrepancyListSchema,
  MetrcInventoryDiscrepancySchema,
  MetrcInventoryDiscrepancySeveritySchema,
  MetrcInventoryDiscrepancyStatusSchema,
  MetrcInventoryPackageReservationSchema,
  MetrcInventoryPackageReservationUpsertSchema,
  MetrcInventorySyncCursorSchema,
  MetrcInventorySyncEndpointSchema,
  MetrcInventorySyncRunSchema,
  MetrcInventorySyncRunStatusSchema,
  MetrcItemMappingListResponseSchema,
  MetrcItemMappingSchema,
  MetrcItemMappingSourceSchema,
  MetrcItemMappingStatusSchema,
  MetrcItemMappingUpsertInputSchema,
  MetrcMatchStrategySchema,
  MetrcPackageHoldActiveSummarySchema,
  MetrcPackageHoldListResponseSchema,
  MetrcPackageHoldNoteSchema,
  MetrcPackageHoldOperationItemSchema,
  MetrcPackageHoldOperationResultSchema,
  MetrcPackageHoldOperationStatusSchema,
  MetrcPackageHoldOperationTargetSchema,
  MetrcPackageHoldOwnerTypeSchema,
  MetrcPackageHoldReasonSchema,
  MetrcPackageHoldSchema,
  MetrcPackageHoldSourceSchema,
  MetrcPackageHoldStatusSchema,
  MetrcPackageMirrorSchema,
  MetrcPackageMirrorUpsertSchema,
  MetrcPackageMovementEvidenceListResponseSchema,
  MetrcPackageMovementEvidenceSchema,
  MetrcPackageMovementEvidenceStatusSchema,
  MetrcPackageMovementEvidenceUpsertInputSchema,
  MetrcPackageMovementTypeSchema,
  MetrcPackagePublicationListResponseSchema,
  MetrcPackagePublicationOperationItemSchema,
  MetrcPackagePublicationOperationResultSchema,
  MetrcPackagePublicationOperationStatusSchema,
  MetrcPackagePublicationSchema,
  MetrcPackagePublicationSourceSchema,
  MetrcPackagePublicationStatusSchema,
  MetrcPackagePublicationTargetSchema,
  MetrcPackagePublicationUpsertInputSchema,
  MetrcPackageStagingAggregatesResponseSchema,
  MetrcPackageStagingAggregatesSchema,
  MetrcPackageStagingListResponseSchema,
  MetrcPackageStagingRowSchema,
  MetrcPackageStagingStateSchema,
  MetrcPackageStateSchema,
  MetrcPackageSyncStateSchema,
  MetrcVariantCaseConfigSchema,
  ProductMetrcPackageAlreadyImportedRowSchema,
  ProductMetrcPackageCandidateSchema,
  ProductMetrcPackageImportConfigSchema,
  ProductMetrcPackageMatchStrategySchema,
  ProductMetrcPackageReconciliationClassSchema,
  ProductMetrcPackageReconciliationRowSchema,
  ProductMetrcVariantCaseConfigSchema,
  PublishMetrcPackagesInputSchema,
  ReleaseMetrcPackageHoldsInputSchema,
  SumActiveMetrcPackageHoldsByInventoryPackageIdsInputSchema,
  SumActiveMetrcPackageHoldsByPackageLabelsInputSchema,
  SyncActiveMetrcPackagesResultSchema,
  SyncMetrcFacilityPackageStatesResultSchema,
  SyncMetrcPackageStateResultSchema,
  SyncOutgoingTransferEvidenceInputSchema,
  SyncOutgoingTransferEvidenceResultSchema,
  TargetedMetrcPackageMovementEvidenceBackfillInputSchema,
  TargetedMetrcPackageMovementEvidenceBackfillResultSchema,
  TargetedMetrcPackageMovementEvidenceBackfillStatusSchema,
  UnpublishMetrcPackagesInputSchema,
  UpdateMetrcItemMappingInputSchema
};
